import { useEffect, useMemo, useState } from 'react'

import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import CursorCardWrapper from 'components/card/CursorCardWrapper'
import PhatDropdown from 'components/formElements/PhatDropdown'
import DeckCardWrapper, { DeckCardWrapperChildProps } from 'components/card/deckCards/deckCardWrapper'
import TabContainer from 'components/elements/TabButtons'

import { generateContextOptions } from 'components/card/deckCards/deckCardContextMenu'

import { useActionless, useAppSelector } from 'redux/hooks'
import { SET_DECK_STATE } from 'redux/deck/actions/types'

import { HUMAN_SORT, HUMAN_STACK } from 'types/deck'
import { setLocalSorting, setLocalStack } from 'services/accountSettings.service'
import { selectStacks } from 'redux/deck/selectors'

import { ctrlConverter } from 'utils/macosLegendHelper'

import styles from './statsContainerCardList.module.scss'
import environment from 'environment'

const sortOptions = Object.keys(HUMAN_SORT).map(text => ({
  text,
  key: HUMAN_SORT[text],
  value: HUMAN_SORT[text],
}))

const stackOptions = Object.keys(HUMAN_STACK).map(text => ({
  text,
  key: HUMAN_STACK[text],
  value: HUMAN_STACK[text],
}))

const StatsContainerCardList = () => {
  const [viewing, setViewing] = useState<'deck' | 'selected' | 'focused'>('focused')

  const { mainDeck } = useAppSelector(selectStacks)
  const sorting = useAppSelector(state => state.deck.sorting)
  const stack = useAppSelector(state => state.deck.stack)
  const multiSelectedIds = useAppSelector(state => state.active.multiSelectedIds)
  const highlightedCards = useAppSelector(state => state.active.highlightedCards)

  const [setDeckState] = useActionless(SET_DECK_STATE)

  const hasMultiSelected = Object.keys(multiSelectedIds).length > 0
  const hasHighlighted = Object.keys(highlightedCards).length > 0

  useEffect(() => {
    if (viewing === 'focused' && !hasHighlighted && hasMultiSelected) setViewing('selected')
    if (viewing === 'selected' && !hasMultiSelected && hasHighlighted) setViewing('focused')
  }, [multiSelectedIds, highlightedCards])

  const handleSortChange = (_e: any, { value }: any) => {
    setDeckState({ sorting: value })
    setLocalSorting(value)
  }

  const handleStackChange = (_e: any, { value }: any) => {
    setDeckState({ stack: value })
    setLocalStack(value)
  }

  let cardIndexOverall = -1

  return (
    <div className={styles.container}>
      <TabContainer
        className={styles.tabContainer}
        optionsClassName={styles.tabButton}
        options={[
          { label: 'Focused', selected: viewing === 'focused', onClick: () => setViewing('focused') },
          { label: 'Selected', selected: viewing === 'selected', onClick: () => setViewing('selected') },
          { label: 'Full deck', selected: viewing === 'deck', onClick: () => setViewing('deck') },
        ]}
      />

      <div className={styles.controls}>
        <PhatDropdown
          inlineHeader
          header="Group by"
          icon="clone outline"
          value={stack}
          triggerWrapperClassName={styles.dropdowns}
          options={stackOptions.map(opt => ({
            label: opt.text,
            id: `${opt.value}`,
            onClick: () => handleStackChange(null, { value: opt.value }),
          }))}
        />

        <PhatDropdown
          inlineHeader
          header="Sort by"
          icon="sort amount up"
          value={sorting}
          triggerWrapperClassName={styles.dropdowns}
          options={sortOptions.map(opt => ({
            label: opt.text,
            id: `${opt.value}`,
            onClick: () => handleSortChange(null, { value: opt.value }),
          }))}
        />
      </div>

      {!hasHighlighted && viewing === 'focused' && <div className={styles.noCards}>Click charts to focus on cards</div>}
      {!hasMultiSelected && viewing === 'selected' && (
        <div className={styles.noCards}>{ctrlConverter('Ctrl')} + Click charts to select cards</div>
      )}

      {mainDeck.map(stack => {
        let sectionFilter = (_cardId: string) => true

        if (viewing === 'selected') sectionFilter = (cardId: string) => multiSelectedIds[cardId]
        if (viewing === 'focused') sectionFilter = (cardId: string) => highlightedCards[cardId]

        const stackHasCards = stack.cardIds.some(sectionFilter)

        return (
          <div key={stack.name} className={styles.stackContainer}>
            {stackHasCards && <label className={styles.stackHeader}>{stack.name}</label>}
            <div className={styles.cardList}>
              {stack.cardIds.map(cardId => {
                if (viewing === 'selected' && !multiSelectedIds[cardId]) return null
                if (viewing === 'focused' && !highlightedCards[cardId]) return null

                // NOTE - this is starting at -1 so the first index is 0
                cardIndexOverall++

                return (
                  <FocusedCardWrapper
                    key={`${viewing}-${cardId}`}
                    cardId={cardId}
                    cardIndexOverall={cardIndexOverall}
                  />
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default StatsContainerCardList

const FocusedCardWrapper = ({ cardId, cardIndexOverall }: { cardId: string; cardIndexOverall: number }) => {
  return <DeckCardWrapper cardId={cardId} stackName="" component={FocusedCard} extraData={{ cardIndexOverall }} />
}

const FocusedCard = ({
  card,
  stackName,
  localCardData,
  deckData,
  userData,
  actions,
  extraData,
}: DeckCardWrapperChildProps) => {
  const handleClick = (e: React.MouseEvent) => {
    const controlKey = environment.getOnMac() ? e.metaKey : e.ctrlKey

    if (controlKey) return actions.multiSelect()

    actions.openCardModal()
  }

  return (
    <div
      className={styles.singleCardRecord}
      style={{
        backgroundColor: localCardData.isMultiSelected ? 'var(--archidekt-orange-transparent)' : undefined,
        opacity: localCardData.antiHighlight ? 0.3 : undefined,
      }}>
      <CursorCardWrapper
        className={styles.cursorWrapper}
        key={card.id}
        card={card}
        skipOnTouch
        insideContainerQueryBlock>
        <button className={styles.primaryCardButton} onClick={handleClick}>
          {localCardData.quantity} {card.name}
        </button>
      </CursorCardWrapper>

      <ArchidektDropdown
        // The 8 is arbitrary, but it's the point where the dropdown starts to got covered by the parents container
        menuClassName={`
          ${styles.dropdownMenu}
          ${extraData.cardIndexOverall > 8 ? styles.dropdownOpenUp : ''}
        `}
        options={generateContextOptions({ card, localCardData, actions, stackName, deckData, userData, extraData: {} })}
      />
    </div>
  )
}
